import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import type { UniclientComponentTitle } from '@lemonade-hq/uniclient-chat';
import { Flex, useDelayedValue } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useState } from 'react';
import { FRAME_TRANSITION_DURATION } from '../animators/durations';
import { WizardComponentHistoryOutputAnimator } from '../animators/WizardComponentHistoryOutputAnimator';
import * as styles from './WizardComponent.css';
import { WizardTitle } from './WizardTitle';

interface WizardComponentProps {
  readonly title?: UniclientComponentTitle;
  readonly titleExitDelay?: number;
  readonly hideTitle?: boolean;
  readonly loading?: boolean;
  readonly children?: React.ReactNode;
  readonly isTransitioningOut: boolean;
  readonly onTransitionEnd: () => void;
  readonly historyOutput: string | undefined;
  readonly onAction?: WizardComponentAdapterProps['onAction'];
  readonly stepId?: string;
}

export const WizardComponent: React.FC<WizardComponentProps> = ({
  title,
  titleExitDelay = 0,
  hideTitle = false,
  children,
  loading,
  isTransitioningOut,
  historyOutput = '',
  onTransitionEnd,
  onAction,
  stepId,
}) => {
  const [isTransitioningOutOnce, setIsTransitioningOutOnce] = useState(false);
  const isTitleVisible = useDelayedValue(!hideTitle, true, titleExitDelay);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isTransitioningOut) {
        setIsTransitioningOutOnce(true);
      }
    }, FRAME_TRANSITION_DURATION * 0.6);

    return () => clearTimeout(timeout);
  }, [isTransitioningOut]);

  return (
    <Flex flexDirection="column" height="100%" justifyContent="start">
      <AnimatePresence initial={false} mode="popLayout">
        {title != null && isTitleVisible && (
          <Flex
            animate={{ opacity: 1 }}
            as={motion.div}
            className={styles.title({
              transitionOut: isTransitioningOutOnce,
            })}
            exit={{ opacity: 0 }}
            flexShrink="0"
            mb={styles.COMPONENT_TITLE_MARGIN}
          >
            <WizardTitle loading={loading} onAction={onAction} title={title} titleKey={stepId} />
          </Flex>
        )}
      </AnimatePresence>

      <WizardComponentHistoryOutputAnimator
        animate={isTransitioningOutOnce}
        onTransitionEnd={onTransitionEnd}
        output={historyOutput}
      >
        {children}
      </WizardComponentHistoryOutputAnimator>
    </Flex>
  );
};
